import { Check, Copy } from 'lucide-react';
import { useEffect, useState } from 'react';
import { cn } from '@/lib/cn';
import { shortId } from '@/lib/format';
import { Tooltip } from './Tooltip';

async function copyText(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}

function useCopied(): [boolean, (value: string) => void] {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1400);
    return () => clearTimeout(timer);
  }, [copied]);
  return [
    copied,
    (value: string) => {
      void copyText(value).then((ok) => {
        if (ok) setCopied(true);
      });
    },
  ];
}

/** A monospaced id that copies its full value on click. */
export function CopyId({
  value,
  display,
  full = false,
  className,
}: {
  value: string;
  /** Text to show instead of the (truncated) id. */
  display?: string;
  full?: boolean;
  className?: string;
}) {
  const [copied, copy] = useCopied();
  return (
    <Tooltip content={copied ? 'Copied' : value}>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          copy(value);
        }}
        className={cn(
          'group inline-flex max-w-full items-center gap-1 rounded-xs text-left text-fg-muted hover:text-fg',
          className,
        )}
        aria-label={`Copy ${value}`}
      >
        <span className="truncate mono">{display ?? (full ? value : shortId(value))}</span>
        {copied ? (
          <Check aria-hidden className="size-3 shrink-0" />
        ) : (
          <Copy
            aria-hidden
            className="size-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-60 group-focus-visible:opacity-60"
          />
        )}
      </button>
    </Tooltip>
  );
}

export function CopyButton({
  value,
  label = 'Copy',
  className,
}: {
  value: string;
  label?: string;
  className?: string;
}) {
  const [copied, copy] = useCopied();
  return (
    <Tooltip content={copied ? 'Copied' : label}>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          copy(value);
        }}
        aria-label={label}
        className={cn(
          'inline-flex size-5 shrink-0 items-center justify-center rounded-xs text-fg-subtle hover:bg-hover hover:text-fg',
          className,
        )}
      >
        {copied ? <Check aria-hidden className="size-3" /> : <Copy aria-hidden className="size-3" />}
      </button>
    </Tooltip>
  );
}
